import { useCallback, useRef, useState } from "react";
import * as tf from "@tensorflow/tfjs";
import {
  BasicPitch,
  noteFramesToTime,
  addPitchBendsToNoteEvents,
  outputToNotesPoly,
  type NoteEventTime,
} from "@spotify/basic-pitch";

export interface PolyNoteEvent {
  pitch: number; // MIDI note number
  startTime: number; // seconds
  endTime: number; // seconds
  confidence: number; // amplitude / loudness proxy
}

interface PolyPitchState {
  isLoading: boolean;
  isReady: boolean;
  isAnalyzing: boolean;
  error: string | null;
  noteEvents: PolyNoteEvent[];
  volume: number;
}

const MODEL_PATH = "/models/basic-pitch/model.json";
/** Basic Pitch está entrenado y asume siempre esta tasa (ver inference.ts del paquete). */
const BASIC_PITCH_SAMPLE_RATE = 22050;

async function resampleMonoTo22050(
  samples: Float32Array,
  sourceRate: number
): Promise<Float32Array> {
  if (!Number.isFinite(sourceRate) || sourceRate <= 0) return samples;
  if (Math.abs(sourceRate - BASIC_PITCH_SAMPLE_RATE) < 1) return samples;

  const durationSec = samples.length / sourceRate;
  const outFrames = Math.max(1, Math.ceil(durationSec * BASIC_PITCH_SAMPLE_RATE));
  const offline = new OfflineAudioContext(1, outFrames, BASIC_PITCH_SAMPLE_RATE);
  const srcBuf = offline.createBuffer(1, samples.length, sourceRate);
  srcBuf.copyToChannel(samples, 0);
  const srcNode = offline.createBufferSource();
  srcNode.buffer = srcBuf;
  srcNode.connect(offline.destination);
  srcNode.start(0);
  const rendered = await offline.startRendering();
  return rendered.getChannelData(0).slice();
}

export function usePolyPitchDetector() {
  const [state, setState] = useState<PolyPitchState>({
    isLoading: false,
    isReady: false,
    isAnalyzing: false,
    error: null,
    noteEvents: [],
    volume: 0,
  });

  const basicPitchRef = useRef<BasicPitch | null>(null);
  const abortRecordingRef = useRef<(() => void) | null>(null);

  // ─── Cargar modelo ───
  const loadModel = useCallback(async () => {
    if (basicPitchRef.current) return;

    setState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      // Forzar CPU para evitar conflicto con MediaPipe GPU en gráficas integradas.
      // Verificamos si ya hay backend para evitar warnings de doble registro
      // (Basic Pitch incluye su propia copia de tfjs).
      if (!tf.getBackend()) {
        await tf.setBackend("cpu");
      }
      await tf.ready();

      basicPitchRef.current = new BasicPitch(MODEL_PATH);
      setState((s) => ({ ...s, isLoading: false, isReady: true }));
    } catch (err) {
      console.error("Error loading Basic Pitch model:", err);
      setState((s) => ({
        ...s,
        isLoading: false,
        error: "No se pudo cargar el modelo de detección polifónica.",
      }));
    }
  }, []);

  const processDetectedNotes = useCallback(
    (rawNotes: NoteEventTime[], chordSnap: boolean): PolyNoteEvent[] => {
      const MIN_MIDI = 28;
      const MAX_MIDI = 96;
      const MIN_DURATION_MS = chordSnap ? 25 : 35;
      const MIN_CONFIDENCE = chordSnap ? 0.06 : 0.1;

      let events: PolyNoteEvent[] = rawNotes
        .map((n: NoteEventTime) => ({
          pitch: n.pitchMidi,
          startTime: n.startTimeSeconds,
          endTime: n.startTimeSeconds + n.durationSeconds,
          confidence: n.amplitude ?? 0.5,
        }))
        .filter((n) => n.pitch >= MIN_MIDI && n.pitch <= MAX_MIDI)
        .filter((n) => (n.endTime - n.startTime) * 1000 >= MIN_DURATION_MS)
        .filter((n) => n.confidence >= MIN_CONFIDENCE);

      if (chordSnap) {
        events.sort((a, b) => b.confidence - a.confidence);
        return events.slice(0, 16);
      }

      const pitchMap = new Map<number, PolyNoteEvent>();
      for (const n of events) {
        const roundedPitch = Math.round(n.pitch);
        const existing = pitchMap.get(roundedPitch);
        if (!existing || n.confidence > existing.confidence) {
          pitchMap.set(roundedPitch, n);
        }
      }
      return Array.from(pitchMap.values()).sort((a, b) => a.pitch - b.pitch);
    },
    []
  );

  // ─── Analizar AudioBuffer ───
  const analyze = useCallback(
    async (audioBuffer: AudioBuffer, chordSnap = false): Promise<PolyNoteEvent[]> => {
      if (!basicPitchRef.current) {
        await loadModel();
      }
      const bp = basicPitchRef.current;
      if (!bp) return [];

      setState((s) => ({ ...s, isAnalyzing: true, noteEvents: [] }));

      try {
        const frames: number[][] = [];
        const onsets: number[][] = [];
        const contours: number[][] = [];

        await bp.evaluateModel(
          audioBuffer,
          (f, o, c) => {
            frames.push(...f);
            onsets.push(...o);
            contours.push(...c);
          },
          () => {
            /* sin spam en consola; el modelo emite muchos frames */
          }
        );

        const onsetTh = chordSnap ? 0.12 : 0.2;
        const frameTh = chordSnap ? 0.1 : 0.18;
        const notes = noteFramesToTime(
          addPitchBendsToNoteEvents(
            contours,
            outputToNotesPoly(frames, onsets, onsetTh, frameTh, chordSnap ? 2 : 3, true, null, null)
          )
        );

        const events = processDetectedNotes(notes, chordSnap);

        setState((s) => ({
          ...s,
          isAnalyzing: false,
          noteEvents: events,
        }));

        return events;
      } catch (err) {
        console.error("Error analyzing audio:", err);
        setState((s) => ({
          ...s,
          isAnalyzing: false,
          error: "Error al analizar el audio.",
        }));
        return [];
      }
    },
    [loadModel, processDetectedNotes]
  );

  /** Analiza muestras del ring buffer mono (sin abrir otro micrófono). */
  const analyzeSamples = useCallback(
    async (
      samples: Float32Array,
      sourceRate: number,
      chordSnap = true
    ): Promise<PolyNoteEvent[]> => {
      if (samples.length < 256) return [];
      if (!basicPitchRef.current) {
        await loadModel();
      }
      const bp = basicPitchRef.current;
      if (!bp) return [];

      setState((s) => ({ ...s, isAnalyzing: true }));

      try {
        const mono22050 = await resampleMonoTo22050(samples, sourceRate);
        const ctx = new AudioContext({ sampleRate: BASIC_PITCH_SAMPLE_RATE });
        const audioBuffer = ctx.createBuffer(1, mono22050.length, BASIC_PITCH_SAMPLE_RATE);
        audioBuffer.copyToChannel(mono22050, 0);
        await ctx.close();

        const frames: number[][] = [];
        const onsets: number[][] = [];
        const contours: number[][] = [];

        await bp.evaluateModel(
          audioBuffer,
          (f, o, c) => {
            frames.push(...f);
            onsets.push(...o);
            contours.push(...c);
          },
          () => {}
        );

        const onsetTh = chordSnap ? 0.12 : 0.2;
        const frameTh = chordSnap ? 0.1 : 0.18;
        const rawNotes = noteFramesToTime(
          addPitchBendsToNoteEvents(
            contours,
            outputToNotesPoly(frames, onsets, onsetTh, frameTh, chordSnap ? 2 : 3, true, null, null)
          )
        );

        const events = processDetectedNotes(rawNotes, chordSnap);
        setState((s) => ({ ...s, isAnalyzing: false, noteEvents: events }));
        return events;
      } catch (err) {
        console.error("Error analyzing samples:", err);
        setState((s) => ({ ...s, isAnalyzing: false, error: "Error al analizar el audio." }));
        return [];
      }
    },
    [loadModel, processDetectedNotes]
  );

  // ─── Grabar desde micrófono y analizar ───
  const stopRecording = useCallback(() => {
    if (abortRecordingRef.current) {
      abortRecordingRef.current();
      abortRecordingRef.current = null;
    }
  }, []);

  const analyzeFromMicrophone = useCallback(
    async (durationMs: number = 3000, chordSnap = false): Promise<PolyNoteEvent[]> => {
      setState((s) => ({ ...s, error: null }));
      let cancelled = false;

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            // AGC puede “pulsar” el nivel y confundir onset en clips cortos
            autoGainControl: false,
            sampleRate: BASIC_PITCH_SAMPLE_RATE,
          },
        });

        const audioContext = new AudioContext({ sampleRate: BASIC_PITCH_SAMPLE_RATE });

        // CRÍTICO: resumir el contexto de audio. En Chrome empieza suspendido
        // hasta que el usuario interactúa o se llama resume().
        if (audioContext.state === "suspended") {
          await audioContext.resume();
        }

        const source = audioContext.createMediaStreamSource(stream);
        const processor = audioContext.createScriptProcessor(4096, 1, 1);
        const chunks: Float32Array[] = [];
        let sumSquares = 0;
        let totalSamples = 0;

        source.connect(processor);
        // El ScriptProcessor solo llama onaudioprocess si el grafo está “activo” y
        // la señal llega hasta un destino. Sin esto, chunks queda vacío y
        // createBuffer(1, 0, rate) lanza NotSupportedError en varios navegadores.
        const mute = audioContext.createGain();
        mute.gain.value = 0;
        processor.connect(mute);
        mute.connect(audioContext.destination);

        processor.onaudioprocess = (e) => {
          if (cancelled) return;
          const data = e.inputBuffer.getChannelData(0);
          chunks.push(new Float32Array(data));
          for (let i = 0; i < data.length; i++) {
            sumSquares += data[i] * data[i];
          }
          totalSamples += data.length;
        };

        // Setup abort handler
        abortRecordingRef.current = () => {
          cancelled = true;
          try {
            processor.disconnect();
            mute.disconnect();
          } catch {
            /* ya desconectado */
          }
          source.disconnect();
          stream.getTracks().forEach((t) => t.stop());
          audioContext.close().catch(() => {});
        };

        const recordMs = Math.max(80, durationMs);
        await new Promise<void>((resolve) => setTimeout(resolve, recordMs));

        // Clean up abort handler if we finished normally
        abortRecordingRef.current = null;

        const volume = totalSamples > 0 ? Math.min(Math.sqrt(sumSquares / totalSamples) * 30, 1) : 0;

        if (cancelled) {
          setState((s) => ({ ...s, volume }));
          return [];
        }

        // Detener grabación
        try {
          processor.disconnect();
          mute.disconnect();
        } catch {
          /* */
        }
        source.disconnect();
        stream.getTracks().forEach((t) => t.stop());
        const captureRate = audioContext.sampleRate;
        await audioContext.close();

        // Concatenar chunks en un solo Float32Array
        const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
        if (totalLength === 0) {
          setState((s) => ({ ...s, volume: 0 }));
          return [];
        }
        const combined = new Float32Array(totalLength);
        let offset = 0;
        for (const chunk of chunks) {
          combined.set(chunk, offset);
          offset += chunk.length;
        }

        // Basic Pitch exige mono 22050 Hz; re-muestrear si el dispositivo entregó otra tasa.
        const mono22050 = await resampleMonoTo22050(combined, captureRate);

        const finalContext = new AudioContext({ sampleRate: BASIC_PITCH_SAMPLE_RATE });
        const audioBuffer = finalContext.createBuffer(1, mono22050.length, BASIC_PITCH_SAMPLE_RATE);
        audioBuffer.copyToChannel(mono22050, 0);
        await finalContext.close();

        const events = await analyze(audioBuffer, chordSnap);
        setState((s) => ({ ...s, volume }));
        return events;
      } catch (err) {
        console.error("Error recording from microphone:", err);
        setState((s) => ({
          ...s,
          error: "No se pudo acceder al micrófono.",
        }));
        return [];
      }
    },
    [analyze]
  );

  return {
    state,
    loadModel,
    stopRecording,
    analyze,
    analyzeSamples,
    analyzeFromMicrophone,
  };
}
